// Story 22.1 (FR88) — the CITS attach of an existing individual clinician to an
// organization's roster.
//
// A FILE OF ITS OWN, and a path helper of its own, because this write is not
// shaped like the others on Screen 21. `adminUserActionPath` builds
// `/admin/users/{id}/{action}` and cannot express this route: the ORGANIZATION
// is the path parameter and the target user travels in the BODY (boundary row
// 10, and `epics.md` / SCP §2 fix the route). Bending the shared helper to
// reach it would make every other caller's path a template of two shapes.
//
// INVALIDATE-AND-REFETCH, never a cache patch. The 200 body says what the attach
// DID; it is not the read model. The seat moves this user's access_state, their
// entitlement source and the organization's four seat figures at once, and only
// the server can restate all of them consistently.

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api-client";
import { ADMIN_CONSOLE_QUERY_KEY } from "@/hooks/queries/useAdminConsole";
import { ADMIN_ORG_QUERY_KEY } from "@/hooks/queries/useAdminOrgDetail";
import { ADMIN_ORG_TOTALS_QUERY_KEY } from "@/hooks/queries/useAdminOrgTotals";
import { ADMIN_ORGS_QUERY_KEY } from "@/hooks/queries/useAdminOrgs";
import { ADMIN_USERS_QUERY_KEY } from "@/hooks/queries/useAdminUsers";
import { adminUserQueryKey } from "@/hooks/queries/useAdminUserDetail";
import { REVERSAL_PREFLIGHT_QUERY_KEY } from "@/hooks/queries/useReversalPreflight";
import type {
  AttachClinicianToOrgData,
  AttachClinicianToOrgRequest,
} from "@/hooks/queries/admin-users-types";

/** `POST /admin/orgs/{org_id}/attach-clinician` — the organization is the path. */
export function attachClinicianPath(orgId: string): string {
  return `/admin/orgs/${encodeURIComponent(orgId)}/attach-clinician`;
}

/**
 * The organization is not known until the dialog is submitted, so it is a
 * MUTATION VARIABLE rather than a hook argument — unlike every
 * `useAdminUserActions` hook, whose target is fixed by the screen's route.
 */
export interface AttachClinicianVariables extends AttachClinicianToOrgRequest {
  organization_id: string;
}

export function useAttachClinicianToOrg(userId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ organization_id, ...body }: AttachClinicianVariables) =>
      apiFetch<AttachClinicianToOrgData>(attachClinicianPath(organization_id), {
        method: "POST",
        body,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminUserQueryKey(userId) });
      void queryClient.invalidateQueries({ queryKey: [ADMIN_USERS_QUERY_KEY] });
      // The organization list too: the attach moves `seat_consumed`,
      // `active_seat_count` and `remaining` on the row the admin just picked,
      // and the picker on the next attach reads those to decide what it may
      // offer. A stale list would offer a seat that no longer exists.
      void queryClient.invalidateQueries({ queryKey: [ADMIN_ORGS_QUERY_KEY] });
      // And the org DETAIL, which is a SEPARATE key — `admin-org`, not
      // `admin-orgs`, so the list invalidation above does not reach it — and the
      // screen that carries the organization's roster and its four seat figures.
      // Screen 24 is one click from here and would otherwise render the
      // pre-attach headcount for as long as its query stayed fresh.
      void queryClient.invalidateQueries({ queryKey: [ADMIN_ORG_QUERY_KEY] });
      void queryClient.invalidateQueries({ queryKey: [ADMIN_CONSOLE_QUERY_KEY] });
      // Story 22.4, from 22.3's own review (H7). The platform org TOTALS are a
      // THIRD key again — `admin-org-totals` — and they sum `seat_consumed`
      // across the estate, which this attach moves. Every other mutation that
      // moves it (renew, extend, adjust-seats, create-organization) already
      // invalidates this key; the attach and its reversal were the two that did
      // not, so the Organizations tile kept printing the pre-attach total.
      void queryClient.invalidateQueries({ queryKey: [ADMIN_ORG_TOTALS_QUERY_KEY] });
      // And the reversal PREFLIGHT, for the same reason 22.3's reversal
      // invalidates it — the other way round.
      //
      // The attach is what MAKES an attachment reversible, and the loop the
      // correction window exists for runs straight through this key: attach,
      // reverse (that hook invalidates, the refetch answers `reversible: false` /
      // ORG-029), then RE-ATTACH the same clinician to the same organization.
      // Without this line the spent answer stays FRESH under the app-wide
      // `staleTime: 30_000` with `refetchOnWindowFocus: false` (`App.tsx`), so
      // reopening Reverse inside that window fires no refetch and
      // `ReverseAttachmentDialog` renders its non-window fall-through — "This
      // clinician does not hold an active seat on this organization" — with
      // Confirm shut on an attachment the server would have reversed.
      //
      // A write that moves eligibility owes the eligibility read an invalidation
      // in BOTH directions, not only in the one that spends it.
      void queryClient.invalidateQueries({
        queryKey: [REVERSAL_PREFLIGHT_QUERY_KEY],
      });
    },
  });
}
